/**
 * People directory (F1): guest-first create/edit (a person exists without an
 * account). Only what each church's `people` capability allows to sync arrives;
 * creating requires `people:write` (the server re-validates).
 */
import { msg } from "@lingui/core/macro";
import { createMemo, createSignal, For, Show } from "solid-js";
import { EXPORT_DELIVERY } from "@fleet/plugins/export";
import { useKernelService } from "@fleet/kernel";
import {
  buildPeopleDataset,
  exportDataset,
  getChurchesQuery,
  getMembershipsQuery,
  getPeopleQuery,
  queryKeys,
} from "@agape/shared";
import {
  type ChurchRecord,
  type MembershipRecord,
  type PersonRecord,
  person as personTable,
} from "@agape/powersync";
import { Download, UserPlus, Users } from "@fleet/icons";

import { Link } from "@tanstack/solid-router";
import { useI18n } from "@/providers/i18n-provider";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@fleet/ui/card";
import { PageSubHeader } from "@/components/ui/page-subheader";
import { Select } from "@/components/ui/select";
import { TextField } from "@fleet/ui/text-field";
import { showToast } from "@/components/ui/toast";
import { Field, FormDialog } from "./form";
import { newId, nowIso, useDomainQuery, useGrants, useWriter } from "../use-domain";

export function PeoplePage() {
  const { t } = useI18n();
  const write = useWriter();
  const grants = useGrants();

  const people = useDomainQuery<PersonRecord>(
    (uid) => queryKeys.domain.people(uid),
    (db) => getPeopleQuery(db),
  );
  const churches = useDomainQuery<ChurchRecord>(
    (uid) => queryKeys.domain.churches(uid),
    (db) => getChurchesQuery(db),
  );
  const memberships = useDomainQuery<MembershipRecord>(
    (uid) => queryKeys.domain.memberships(uid),
    (db) => getMembershipsQuery(db),
  );

  const [search, setSearch] = createSignal("");
  const filtered = createMemo(() => {
    const q = search().trim().toLowerCase();
    const rows = people.data ?? [];
    if (!q) {
      return rows;
    }
    return rows.filter((p) => [p.name, p.email, p.phone].some((v) => v?.toLowerCase().includes(q)));
  });

  // Churches where the account can write the directory.
  const writableChurches = createMemo(() =>
    (churches.data ?? []).filter((c) => grants.can([c.id], "people", "write")),
  );

  const [open, setOpen] = createSignal(false);
  const [name, setName] = createSignal("");
  const [email, setEmail] = createSignal("");
  const [phone, setPhone] = createSignal("");
  const [churchId, setChurchId] = createSignal("");

  const create = async () => {
    const target = churchId() || writableChurches()[0]?.id;
    if (!name().trim() || !target) {
      return;
    }
    await write()
      .insert(personTable)
      .values({
        id: newId(),
        church_id: target,
        name: name().trim(),
        email: email().trim() || null,
        phone: phone().trim() || null,
        created_at: nowIso(),
        updated_at: nowIso(),
      });
    setOpen(false);
    setName("");
    setEmail("");
    setPhone("");
    showToast(t(msg`Person added to the directory`), "success");
  };

  const churchName = (id: string | null) =>
    (churches.data ?? []).find((c) => c.id === id)?.name ?? "";

  /**
   * PORTABILITY (sheet §6): the directory can ALWAYS be taken with you. Exports
   * what the account has synced — not one row more, so it respects the
   * permissions engine by construction — applying the active search filter.
   * Available on ALL plans, including free: the monetization kit re-injects
   * `export` on every tier and tests pin that.
   */
  const delivery = useKernelService(EXPORT_DELIVERY);

  const exportDirectory = () => {
    const target = delivery();
    if (!target) {
      return;
    }
    const membershipOf = (personId: string) =>
      (memberships.data ?? []).find((m) => m.person_id === personId);
    const dataset = buildPeopleDataset(
      filtered().map((p) => {
        const m = membershipOf(p.id);
        return {
          name: p.name,
          churchName: churchName(p.church_id),
          email: p.email,
          phone: p.phone,
          address: p.address,
          birthdate: p.birthdate,
          notes: p.notes,
          status: m?.status ?? null,
          baptized: m ? Boolean(m.baptized) : null,
          baptized_at: m?.baptized_at ?? null,
          serves: m ? Boolean(m.serves) : null,
          is_leader: m ? Boolean(m.is_leader) : null,
          is_pastor: m ? Boolean(m.is_pastor) : null,
        };
      }),
      { title: "Directory" },
    );
    void exportDataset(dataset, "csv", target);
    showToast(t(msg`Directory exported`), "success");
  };

  return (
    <div class="space-y-4">
      <PageSubHeader title={t(msg`People`)} />
      <div class="flex items-center justify-between gap-3">
        <TextField
          value={search()}
          onInput={(e) => setSearch(e.currentTarget.value)}
          placeholder={t(msg`Search by name, email or phone…`)}
          class="max-w-xs"
          data-testid="people-search"
        />
        <div class="flex items-center gap-2">
          <Show when={filtered().length > 0}>
            <Button
              size="sm"
              variant="outline"
              data-testid="people-export-csv"
              onClick={exportDirectory}
            >
              <Download size={16} /> {t(msg`Export CSV`)}
            </Button>
          </Show>
          <Show when={writableChurches().length > 0}>
            <Button size="sm" data-testid="person-create" onClick={() => setOpen(true)}>
              <UserPlus size={16} /> {t(msg`New person`)}
            </Button>
          </Show>
        </div>
      </div>
      <Show
        when={filtered().length > 0}
        fallback={
          <p data-testid="people-empty" class="text-muted-foreground">
            {t(msg`No people synced to this account yet.`)}
          </p>
        }
      >
        <div class="space-y-2" data-testid="people-list">
          <For each={filtered()}>
            {(p) => (
              <Link to="/people/$personId" params={{ personId: p.id }} class="block">
                <Card
                  data-testid="person-item"
                  data-person-id={p.id}
                  class="transition-colors hover:border-primary/40"
                >
                  <CardContent class="flex items-center gap-3 py-3">
                    <span class="flex size-8 shrink-0 items-center justify-center rounded-full bg-accent text-accent-foreground">
                      <Show when={p.photo} fallback={<Users size={16} />}>
                        <img
                          src={p.photo ?? ""}
                          alt=""
                          loading="lazy"
                          decoding="async"
                          class="size-8 rounded-full object-cover"
                        />
                      </Show>
                    </span>
                    <div class="min-w-0 flex-1">
                      <p class="truncate font-medium">{p.name}</p>
                      <p class="truncate text-sm text-muted-foreground">
                        {[p.email, p.phone, churchName(p.church_id)].filter(Boolean).join(" · ")}
                      </p>
                    </div>
                  </CardContent>
                </Card>
              </Link>
            )}
          </For>
        </div>
      </Show>

      <FormDialog
        open={open()}
        onOpenChange={setOpen}
        title={t(msg`New person`)}
        icon={<UserPlus size={20} />}
        submitTestId="person-create-submit"
        disabled={!name().trim()}
        onSubmit={create}
      >
        <Field
          id="p-name"
          label={t(msg`Name`)}
          value={name()}
          onInput={setName}
          testId="person-name-input"
        />
        <div class="grid grid-cols-2 gap-3">
          <Field
            id="p-email"
            label={t(msg`Email`)}
            type="email"
            value={email()}
            onInput={setEmail}
          />
          <Field id="p-phone" label={t(msg`Phone`)} value={phone()} onInput={setPhone} />
        </div>
        <Show when={writableChurches().length > 1}>
          <Field id="p-church" label={t(msg`Church`)}>
            <Select
              options={writableChurches().map((c) => ({ value: c.id, label: c.name ?? c.id }))}
              value={churchId() || writableChurches()[0]?.id}
              onChange={setChurchId}
              aria-label={t(msg`Church`)}
            />
          </Field>
        </Show>
      </FormDialog>
    </div>
  );
}
